import { FileCategory, IFile } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { Extend } from '@/utils/typings/extend'
import { toast } from '@/features/toast/toast'
import { useTrackDownloadFilesMutation } from '@/api/files/track-download-files-mutation'
import { useCallback } from 'react'
import { createImageUrl } from '@/utils/image/create-image-url'
import { useFreeTestGuard } from '@/features/subscription/hooks/use-free-test-guard'
import { usePrepareEditorCompatibleFileMutation } from '@/api/files/prepare-editor-compatible-file-mutation'

const getStreamSaver = async () => {
    const streamSaver = await import('streamsaver')
    return streamSaver.default
}

const getDownloadZip = async () => {
    const { downloadZip } = await import('client-zip')
    return downloadZip
}

interface IDownloadFilesArgs {
    files: IFile[] | { file: IFile; fileName?: string }[]
    zipName?: string
}

export interface IDownloadFileItem {
    file: IFile
    fileName?: string
}

interface IPreparedEditorCompatibleFile {
    status: 'READY' | 'PROCESSING'
    url?: string
    type?: string
}

export const shouldPrepareEditorCompatibleDownload = (file: IFile) =>
    file.type === 'webm' && file.hasAlpha === true && !file.editorCompatibleUrl

type IPreparationToastId = ReturnType<typeof toast.loading>

export const notifyEditorCompatiblePreparationStarted = () =>
    toast.loading('Preparing CapCut-compatible video', {
        description: 'This can take a few minutes. Keep this tab open—the download will start automatically.',
        duration: Infinity,
        datadog: { method: 'info' },
    })

export const notifyEditorCompatiblePreparationReady = (toastId: IPreparationToastId) =>
    toast.loading('CapCut-compatible video ready', {
        id: toastId,
        description: 'Starting your download…',
        duration: Infinity,
        datadog: { method: 'info' },
    })

export const notifyEditorCompatiblePreparationFailure = (toastId?: IPreparationToastId) => {
    const message = 'Could not prepare the CapCut-compatible video. Please try again.'
    return toastId === undefined ? toast.error(message) : toast.error(message, { id: toastId })
}

export const prepareEditorCompatibleDownloads = async (
    files: IDownloadFileItem[],
    prepare: (fileUuid: string) => Promise<IPreparedEditorCompatibleFile>
) => {
    const filesToDownload: IDownloadFileItem[] = []
    for (const item of files) {
        if (!shouldPrepareEditorCompatibleDownload(item.file)) {
            filesToDownload.push(item)
            continue
        }

        const compatible = await prepare(item.file.uuid)
        if (compatible.status !== 'READY' || !compatible.url || !compatible.type) {
            throw new Error('Editor-compatible file was not ready')
        }
        filesToDownload.push({
            ...item,
            file: {
                ...item.file,
                editorCompatibleUrl: compatible.url,
                editorCompatibleType: compatible.type,
            },
        })
    }
    return filesToDownload
}

export const useDownloadFiles = () => {
    const { mutateAsync: trackDownloadFilesAsync } = useTrackDownloadFilesMutation()
    const { mutateAsync: prepareEditorCompatibleFile } = usePrepareEditorCompatibleFileMutation()
    const freeTestGuard = useFreeTestGuard('Start a free, 5-day trial to download assets', 'file_download')

    const handleDownloadFilesWithoutGuard = useCallback(
        async ({ files, zipName }: IDownloadFilesArgs) => {
            const normalizedFiles = files
                .filter((item): item is NonNullable<typeof item> => item != null)
                .map((item) => {
                    if ('url' in item && !('file' in item)) {
                        return { file: item as IFile, fileName: item.name }
                    }

                    return item as { file: IFile; fileName?: string }
                })
                .filter((item) => item.file != null)

            if (normalizedFiles.length !== files.length) {
                console.error('Some files are not valid', files)
                toast.error('Some files are not valid')
                return
            }

            if (normalizedFiles.length === 0) {
                console.error('No files to download', files)
                toast.error('No files to download')
                return
            }

            const requiresEditorCompatiblePreparation = normalizedFiles.some((item) =>
                shouldPrepareEditorCompatibleDownload(item.file)
            )
            const preparationToastId = requiresEditorCompatiblePreparation
                ? notifyEditorCompatiblePreparationStarted()
                : undefined

            let filesToDownload: typeof normalizedFiles
            try {
                filesToDownload = await prepareEditorCompatibleDownloads(normalizedFiles, prepareEditorCompatibleFile)
            } catch (error) {
                console.error('Failed to prepare editor-compatible download:', error)
                notifyEditorCompatiblePreparationFailure(preparationToastId)
                return
            }

            if (preparationToastId !== undefined) {
                notifyEditorCompatiblePreparationReady(preparationToastId)
            }

            try {
                trackDownloadFilesAsync(filesToDownload.map((item) => item.file.uuid)).catch((e) => {
                    console.warn('Error tracking download files:', e)
                })
            } catch (e) {
                console.warn('Error tracking download files:', e)
            }

            try {
                if (filesToDownload.length > 1) {
                    return await downloadMultipleFiles({
                        files: filesToDownload.map((item, i) => ({
                            file: item.file,
                            fileName: item.fileName || `Export ${i + 1}.${item.file.type}`,
                        })),
                        zipName: zipName || 'exports',
                    })
                }

                return await downloadSingleFile(filesToDownload[0])
            } finally {
                if (preparationToastId !== undefined) {
                    toast.dismiss(preparationToastId)
                }
            }
        },
        [prepareEditorCompatibleFile, trackDownloadFilesAsync]
    )

    const handleDownloadFiles = useCallback(
        (args: IDownloadFilesArgs) => {
            if (freeTestGuard()) return
            handleDownloadFilesWithoutGuard(args)
        },
        [freeTestGuard, handleDownloadFilesWithoutGuard]
    )

    return handleDownloadFiles
}

type IDownloadMultipleFilesParams = {
    files: { file: IFile; fileName: string }[]
    zipName?: string
}

/**
 * Async generator that yields file inputs for client-zip.
 * This enables lazy fetching as files are fetched one at a time.
 */
async function* generateFileInputs(files: { file: IFile; fileName: string }[]) {
    const usedNames = new Map<string, number>()
    for (const { file, fileName } of files) {
        if (!file.url) continue

        const { url, type } = getFileDownloadInfo(file)
        const downloadFileName = deduplicateFileName(
            createDownloadFileName({ name: fileName, url: file.url, type }),
            usedNames
        )

        const response = await fetchDownloadResponse(url)

        yield {
            name: downloadFileName,
            input: response,
        }
    }
}

/**
 * Downloads multiple files as a streaming ZIP archive.
 *
 * Uses client-zip to generate the ZIP on-the-fly and streamsaver to stream
 * directly to disk without buffering the entire archive in RAM.
 *
 * Traffic flow: CloudFront → Browser → client-zip → streamsaver → Disk
 *
 * Supports all modern browsers. For browsers that don't support streaming, it will fallback to blob download.
 */
const downloadMultipleFiles = async ({ files, zipName }: IDownloadMultipleFilesParams) => {
    try {
        toast.success('Preparing download...')

        const validFiles = files.filter(({ file }) => !!file.url)

        if (validFiles.length === 0) {
            toast.error('No files to download')
            return
        }

        const downloadZip = await getDownloadZip()
        const zipResponse = downloadZip(generateFileInputs(validFiles))
        const zipFileName = `${zipName ?? 'download'}.zip`

        const streamingSucceeded = await streamToDisk(zipResponse, zipFileName)

        if (!streamingSucceeded) {
            const fallbackZipResponse = downloadZip(generateFileInputs(validFiles))
            await fallbackBlobDownload(fallbackZipResponse, zipFileName)
        }

        toast.success('Download started')
    } catch (error) {
        console.error('Error downloading files:', error)
        toast.error('Error downloading zip file')
    }
}

/**
 * Stream a response directly to disk using StreamSaver.
 * Returns true if streaming succeeded, false if fallback is needed.
 *
 * Supports all modern browsers.
 */
const streamToDisk = async (response: Response, fileName: string): Promise<boolean> => {
    try {
        if (!response.body) {
            return false
        }

        const streamSaver = await getStreamSaver()
        const fileStream = streamSaver.createWriteStream(fileName)

        await response.body.pipeTo(fileStream)

        return true
    } catch (error) {
        console.warn('Streaming to disk failed, falling back to blob download:', error)
        return false
    }
}

/**
 * Fallback for browsers that don't support streaming.
 * Buffers the entire response in memory before downloading.
 */
const fallbackBlobDownload = async (response: Response, fileName: string): Promise<void> => {
    console.warn('Streaming to disk failed, falling back to blob download')
    const blob = await response.blob()
    triggerBrowserDownload(blob, fileName)
}

const fetchDownloadResponse = async (url: string) => {
    const response = await fetch(url, { cache: 'no-store' })

    if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`)
    }

    return response
}

const aspectRatioEquivalent = (
    aspectRatio: number,
    goToSize: { width: number; height: number } = { width: 1920, height: 1080 }
): { width: number; height: number } => {
    if (aspectRatio > 1) {
        return { width: goToSize.width, height: goToSize.width / aspectRatio }
    } else {
        return { width: goToSize.height, height: goToSize.height / aspectRatio }
    }
}

/**
 * Get the download URL and type for a file.
 * For images, transforms via CloudFront image service for optimal format.
 */
export const getFileDownloadInfo = (file: IFile): { url: string; type: string } => {
    // Transparent Talking Heads retain WebM for browser playback but expose a PNG-in-MOV
    // rendition for native editors. Apply it at the shared download boundary so
    // single downloads, bulk ZIPs, agent downloads, and generation downloads behave alike.
    if (file.editorCompatibleUrl) {
        return {
            url: file.editorCompatibleUrl,
            type: file.editorCompatibleType ?? 'mov',
        }
    }

    if (file.category === FileCategory.IMAGE && file.type !== 'svg') {
        const format = ['png', 'webp'].includes(file.type) ? 'png' : 'jpeg'
        const url = createImageUrl(file.url, {
            format,
            ...(file.aspectRatio ? aspectRatioEquivalent(file.aspectRatio) : {}),
            quality: format === 'jpeg' ? 75 : undefined,
            resolution: 1,
        })
        return { url, type: format }
    }

    return { url: file.url, type: file.type }
}

export const triggerBrowserDownload = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')

    a.href = url
    a.download = fileName

    document.body.appendChild(a)

    a.click()

    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
}

/**
 * Fetches a url to disk via streaming (falling back to a buffered blob) — the shared single-file
 * download primitive behind `downloadSingleFile` and any other single-url download (e.g. the video
 * editor's render download). Bypasses the HTTP cache so the browser never reuses an opaque response
 * previously cached by a media element.
 *
 * Never throws: a failed fetch resolves `false` instead. This matters beyond error-reporting — a
 * bare `<a download href={crossOriginUrl}>` is the alternative, but browsers ignore the `download`
 * attribute on a cross-origin url (e.g. a signed CloudFront link) and follow it as a normal
 * navigation instead, replacing the whole app. Routing every download through an actual fetch keeps
 * the click from ever navigating, success or failure.
 */
export const downloadUrlToDisk = async (url: string, fileName: string): Promise<boolean> => {
    try {
        const response = await fetchDownloadResponse(url)
        const streamingSucceeded = await streamToDisk(response, fileName)

        if (!streamingSucceeded) {
            const fallbackResponse = await fetchDownloadResponse(url)
            await fallbackBlobDownload(fallbackResponse, fileName)
        }

        return true
    } catch (error) {
        console.error('Error downloading file:', error)
        return false
    }
}

type IDownloadFileParams = {
    file: IFile
    fileName?: string
}

/**
 * Downloads a single file.
 *
 * Bypasses the HTTP cache so the browser does not reuse an opaque response
 * previously cached by a media element.
 */
const downloadSingleFile = async ({ file, fileName }: IDownloadFileParams) => {
    const { url: downloadUrl, type: downloadType } = getFileDownloadInfo(file)

    if (!downloadUrl) return null

    const downloadFileName = createDownloadFileName({
        name: fileName ?? file.name,
        url: file.url,
        type: downloadType,
    })

    const succeeded = await downloadUrlToDisk(downloadUrl, downloadFileName)

    if (succeeded) {
        toast.success('Download started')
    } else {
        toast.error('Error downloading file')
    }
}

/**
 * Ensures zip entry names are unique by appending " (2)", " (3)", etc.
 * Duplicate entry names collapse into a single file when the archive is extracted.
 */
function deduplicateFileName(fileName: string, usedNames: Map<string, number>) {
    const count = usedNames.get(fileName) ?? 0
    usedNames.set(fileName, count + 1)

    if (count === 0) return fileName

    const dotIndex = fileName.lastIndexOf('.')
    if (dotIndex <= 0) return `${fileName} (${count + 1})`

    return `${fileName.slice(0, dotIndex)} (${count + 1})${fileName.slice(dotIndex)}`
}

export function createDownloadFileName(file: Extend<Pick<IFile, 'url' | 'name' | 'type'>>) {
    // Characters that are invalid in filenames across Windows, macOS, and Linux
    // Includes: < > : " / \ | ? * . and control characters (0x00-0x1F)
    const INVALID_FILENAME_CHARS = /[<>:"/\\|?*.\u0000-\u001F]/g

    // Extract file extension from URL (everything after the last dot, before query params)
    const urlWithoutParams = file.url.split('?')[0]
    const fileExtension = urlWithoutParams.includes('.') ? urlWithoutParams.split('.').pop() || '' : ''

    // Get base name from file.name or URL
    let baseName = file.name || urlWithoutParams.split('/').pop() || 'file'

    // Remove extension from baseName if it exists (case-insensitive)
    if (fileExtension && baseName.toLowerCase().endsWith(`.${fileExtension.toLowerCase()}`)) {
        baseName = baseName.slice(0, -(fileExtension.length + 1))
    }

    // Sanitize the base name by replacing special characters (including dots)
    baseName = baseName.replace(INVALID_FILENAME_CHARS, '-').trim()

    // Fallback if name becomes empty after sanitization
    if (!baseName) {
        baseName = 'file'
    }

    return file.type ? `${baseName}.${file.type}` : baseName
}
